// TP-Friendlies API client (club-only club↔club friendly matchmaking).
//
// Isolated from the shared services/api.js on purpose (small PR surface, no risk
// to the shared axios instance) — mirrors services/clubNetworkApi.js and
// services/lastMinuteService.js. Every path is READ from the M1 backend contract
// (dashboard/friendly_views.py, router basename `friendlies/…`); all endpoints
// are gated club-only server-side (agents excluded API-wide).
//
// Mock switch: REACT_APP_FR_MOCK === "1" serves everything from
// services/friendliesMock.js (identical shapes) so the UI runs without a backend.
import axios from "axios";
import friendliesMock from "./friendliesMock";

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;
const USE_MOCK = process.env.REACT_APP_FR_MOCK === "1";

const authApi = axios.create({ baseURL: API_BASE_URL, withCredentials: true });

// Same Bearer-token attachment the main client uses (authSlice persists
// localStorage "tokens" = { access, refresh }).
authApi.interceptors.request.use((config) => {
  try {
    const tokens = JSON.parse(localStorage.getItem("tokens") || "null");
    if (tokens?.access) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${tokens.access}`;
    }
  } catch (e) {
    /* no token available — request proceeds and the server returns 401 */
  }
  return config;
});

const fail = (error) => ({
  success: false,
  status: error?.response?.status,
  error:
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    "Something went wrong. Please try again.",
});

// ── Feed (browse + apply + saved) — base /dashboard/friendlies/feed/ ──────────
// Feed cards carry BOTH the viewer-scoped `saved` bookmark AND `my_application`
// state INLINE (the viewset computes them per-viewer), so — unlike Club-Network —
// there is NO batch side-channel to reconcile. The save endpoint is a single
// idempotent TOGGLE that returns the new `{saved}` state.
export const friendliesService = {
  // Browse OPEN requests. Supported server params (friendly_views._apply_filters):
  // team_category, date_from, date_to, country, region, tier, saved_only.
  // Response: { results, count, meta: { buckets } } — buckets power the week strip.
  getFeed: async (params, signal) => {
    if (USE_MOCK) return friendliesMock.getFeed(params);
    try {
      const res = await authApi.get("/dashboard/friendlies/feed/", { params, signal });
      return {
        success: true,
        data: res.data?.results || [],
        count: res.data?.count ?? 0,
        buckets: res.data?.meta?.buckets || [],
      };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return fail(error);
    }
  },

  // Detail (any status — used later for repost/quick-view).
  getRequest: async (id, signal) => {
    if (USE_MOCK) return friendliesMock.getRequest(id);
    try {
      const res = await authApi.get(`/dashboard/friendlies/feed/${id}/`, { signal });
      return { success: true, data: res.data };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return fail(error);
    }
  },

  // One-tap apply (+ optional note). The auto-message is built SERVER-side from the
  // applying club's own data and returned as `auto_message`. Double-apply is a calm
  // no-op: { already_applied: true, state }.
  apply: async (id, { note = "" } = {}) => {
    if (USE_MOCK) return friendliesMock.apply(id, { note });
    try {
      const body = {};
      if (note) body.note = note;
      const res = await authApi.post(`/dashboard/friendlies/feed/${id}/apply/`, body);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // Toggle the Saved bookmark. Idempotent server-side (create-or-delete the join
  // row) → returns the NEW state { saved: true|false }. No blind branching needed.
  toggleSave: async (id) => {
    if (USE_MOCK) return friendliesMock.toggleSave(id);
    try {
      const res = await authApi.post(`/dashboard/friendlies/feed/${id}/save/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // The Saved view: the viewer's bookmarked OPEN requests + a count.
  getSaved: async (signal) => {
    if (USE_MOCK) return friendliesMock.getSaved();
    try {
      const res = await authApi.get("/dashboard/friendlies/feed/saved/", { signal });
      return { success: true, data: res.data?.results || [], count: res.data?.count ?? 0 };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return fail(error);
    }
  },

  // ── Composer + management (owner) — base /dashboard/friendlies/requests/ ─────
  // Create / edit a friendly request. Body mirrors the M1 composer validation.
  createRequest: async (body) => {
    if (USE_MOCK) return friendliesMock.createRequest(body);
    try {
      const res = await authApi.post("/dashboard/friendlies/requests/", body);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },
  updateRequest: async (id, body) => {
    if (USE_MOCK) return friendliesMock.updateRequest(id, body);
    try {
      const res = await authApi.patch(`/dashboard/friendlies/requests/${id}/`, body);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },
  // Owner detail (repost prefill — carries the raw composer fields).
  getOwnedRequest: async (id) => {
    if (USE_MOCK) return friendliesMock.getRequest(id);
    try {
      const res = await authApi.get(`/dashboard/friendlies/requests/${id}/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },
  // My friendly requests (management tabs). state = open|matched|expired|closed|"".
  getMine: async (state, signal) => {
    if (USE_MOCK) return friendliesMock.getMine(state);
    try {
      const res = await authApi.get("/dashboard/friendlies/requests/", {
        params: state ? { state } : {},
        signal,
      });
      return { success: true, data: res.data?.results || [], count: res.data?.count ?? 0 };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return fail(error);
    }
  },
  // The owner's applications list for one request (dismissed rows already left it).
  getApplications: async (requestId, signal) => {
    if (USE_MOCK) return friendliesMock.getApplications(requestId);
    try {
      const res = await authApi.get(`/dashboard/friendlies/requests/${requestId}/applications/`, { signal });
      return { success: true, data: res.data?.results || [], count: res.data?.count ?? 0 };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return fail(error);
    }
  },
  // Close a request: outcome = 'matched' | 'just_close'.
  closeRequest: async (id, outcome) => {
    if (USE_MOCK) return friendliesMock.closeRequest(id, outcome);
    try {
      const res = await authApi.post(`/dashboard/friendlies/requests/${id}/close/`, { outcome });
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // ── Applications (applicant three-state + owner per-application actions) ─────
  // My applications (applicant): EXACTLY three states, never a rejection signal.
  getMyApplications: async (signal) => {
    if (USE_MOCK) return friendliesMock.getMyApplications();
    try {
      const res = await authApi.get("/dashboard/friendlies/applications/", { signal });
      return { success: true, data: res.data?.results || [], count: res.data?.count ?? 0 };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return fail(error);
    }
  },
  // Owner: open the EXISTING club↔club chat with the applicant → { channel_id }.
  startChat: async (applicationId) => {
    if (USE_MOCK) return friendliesMock.startChat(applicationId);
    try {
      const res = await authApi.post(`/dashboard/friendlies/applications/${applicationId}/start-chat/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },
  // Owner: silent dismiss — the card leaves the list, the applicant is never told.
  dismissApplication: async (applicationId) => {
    if (USE_MOCK) return friendliesMock.dismissApplication(applicationId);
    try {
      const res = await authApi.post(`/dashboard/friendlies/applications/${applicationId}/dismiss/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },
  // Owner (camp only): mark an application agreed → request STAYS OPEN + count++.
  markAgreed: async (applicationId) => {
    if (USE_MOCK) return friendliesMock.markAgreed(applicationId);
    try {
      const res = await authApi.post(`/dashboard/friendlies/applications/${applicationId}/mark-agreed/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },
};

export const FRIENDLIES_MOCK_ENABLED = USE_MOCK;
